import React, { useState } from 'react';
import {
  Container,
  Box,
  Paper,
  TextField,
  Button,
  Link,
  Grid,
  Alert
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/apiServices';
import { useUser } from '../context/UserContext';
import { validatePasswordStrength } from '../utils/passwordUtils';
import { sanitizeInput } from '../utils/validationUtils';

const Login = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { refreshUserProfile } = useUser();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });

    // Validate password strength in real-time
    if (e.target.name === 'password') {
      const validation = validatePasswordStrength(e.target.value);
      if (!validation.isValid && e.target.value.length > 0) {
        e.target.setCustomValidity(validation.message);
      } else {
        e.target.setCustomValidity('');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate password before submitting
    const passwordValidation = validatePasswordStrength(formData.password);
    if (!passwordValidation.isValid) {
      setError(passwordValidation.message);
      setLoading(false);
      return;
    }

    try {
      const response = await authService.login(formData);

      // Handle user profile
      if (response.data) {
        let userProfile = response.data.userProfile;
        // If userProfile is not directly in the login response, fetch it
        if (!userProfile) {
          try {
            const profileResponse = await authService.getProfile();
            userProfile = profileResponse.data;
          } catch (profileError) {
            console.error('Failed to fetch user profile after login:', profileError);
            // Optionally handle this error, e.g., by logging out or showing a specific message
          }
        }

        // Update UserContext state (only in-memory storage)
        if (userProfile) {
          refreshUserProfile(userProfile); // Update UserContext state
        }

        navigate('/'); // Redirect to dashboard
      }
    } catch (err) {
      let errorMessage = 'Login failed. Please try again.';

      // Handle different error types
      if (err.response) {
        // Server responded with error status
        if (err.response.status === 401) {
          errorMessage = err.response.data?.message || 'Incorrect username or password';
        } else if (err.response.status === 500) {
          errorMessage = 'Server error. Please try again later.';
        } else if (err.response.data?.message) {
          errorMessage = err.response.data.message;
        }
      } else if (err.request) {
        // Request was made but no response received
        errorMessage = 'Network error. Please check your connection and try again.';
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box sx={{ mb: 2 }}>
            <img src="/deal-logo.png" alt="Deal Logistics Logo" style={{ maxHeight: 80, width: 'auto' }} />
          </Box>

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
            {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{sanitizeInput(error)}</Alert>}
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label="Username"
              name="username"
              autoComplete="username"
              autoFocus
              value={formData.username}
              onChange={handleChange}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                mt: 3,
                mb: 2,
                backgroundColor: 'rgb(253, 197, 31)',
                '&:hover': {
                  backgroundColor: 'rgb(228, 177, 28)', // Slightly darker shade for hover effect
                }
              }}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
            <Grid container justifyContent="flex-end">
              <Grid>
                <Link href="#" variant="body2" underline="hover">
                  Forgot password?
                </Link>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;